import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useWishlist } from '../../contexts/WishlistContext';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { Button } from './button';
import { Badge } from './badge-new';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, TooltipArrow } from './tooltip';

import { AspectRatio } from './aspect-ratio';
import { Avatar, AvatarImage, AvatarFallback } from './avatar';
import { cn, getImageUrl } from '../../lib/utils';
import {
  Heart,
  HeartIcon,
  ShoppingCart,
  Star,
  StarIcon,
  Eye,
  ShoppingBag,
  Zap,
  Clock,
  Package,
  Leaf
} from 'lucide-react';

const CartPlusIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
    <path d="M19 12v6m-3-3h6" />
  </svg>
);

const ProductCard = ({ product, className }) => {
  // Add null check for product
  if (!product || !product._id) {
    return null;
  }

  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isWishlistActionLoading, setIsWishlistActionLoading] = useState(false);
  const [isCartActionLoading, setIsCartActionLoading] = useState(false);
  const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlist();
  const { addToCart, isInCart } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Helper function to check if product is out of stock (stock is exactly 0 or less)
  const isOutOfStock = () => {
    return (product.stock ?? 0) <= 0;
  };

  // Display stars based on rating with modern design
  const renderStars = (rating = 0) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <Star
          key={`full-${i}`}
          className="h-3.5 w-3.5 fill-amber-400 text-amber-400"
        />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <div key="half" className="relative">
          <StarIcon className="h-3.5 w-3.5 text-gray-300" />
          <Star className="absolute inset-0 h-3.5 w-3.5 fill-amber-400 text-amber-400 overflow-hidden" style={{ clipPath: 'inset(0 50% 0 0)' }} />
        </div>
      );
    }

    const emptyStars = 5 - stars.length;
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <StarIcon
          key={`empty-${i}`}
          className="h-3.5 w-3.5 text-gray-300"
        />
      );
    }

    return <div className="flex items-center gap-0.5">{stars}</div>;
  };

  // Helper function to calculate display price
  const getDisplayPrice = () => {
    const now = new Date();

    // Check if near expiry is active and has expiry date. Checked before the
    // effectivePrice shortcut below, since the backend virtual doesn't
    // account for near-expiry pricing - matching the precedence already
    // used at checkout (order.controller.js) and on the product detail page.
    if (product.isNearExpiry && product.expiryDate && product.nearExpiryDiscountValue) {
      const expiryDate = new Date(product.expiryDate);
      if (now <= expiryDate) {
        if (product.nearExpiryDiscountType === 'percentage') {
          return Math.round(product.regularPrice * (1 - product.nearExpiryDiscountValue / 100));
        } else {
          return product.regularPrice - product.nearExpiryDiscountValue;
        }
      }
    }

    if (product.effectivePrice !== undefined) {
      return product.effectivePrice;
    }

    // Check if sale is active and within date range
    const isSaleActive = product.isSaleActive &&
      product.discountPercentage &&
      product.saleStartDate &&
      product.saleEndDate &&
      now >= new Date(product.saleStartDate) &&
      now <= new Date(product.saleEndDate);

    // Sale discount takes priority if active
    if (isSaleActive) {
      const salePrice = product.regularPrice * (1 - product.discountPercentage / 100);
      return Math.round(salePrice);
    }

    if (product.specialOfferPrice && product.specialOfferPrice < (product.regularPrice || product.price)) {
      return product.specialOfferPrice;
    }

    if (product.discountValue > 0 && product.regularPrice) {
      if (product.discountType === 'percentage') {
        return product.regularPrice * (1 - product.discountValue / 100);
      } else {
        return product.regularPrice - product.discountValue;
      }
    }

    return product.regularPrice || product.price || 0;
  };

  // Calculate discount percentage for display
  const getDiscountPercentage = () => {
    const now = new Date();

    // Check if near expiry is active and has expiry date
    if (product.isNearExpiry && product.expiryDate && product.nearExpiryDiscountValue) {
      const expiryDate = new Date(product.expiryDate);
      if (now <= expiryDate) {
        if (product.nearExpiryDiscountType === 'percentage') {
          return product.nearExpiryDiscountValue;
        } else {
          return Math.round((product.nearExpiryDiscountValue / product.regularPrice) * 100);
        }
      }
    }

    // Check if sale is active and within date range
    const isSaleActive = product.isSaleActive &&
      product.discountPercentage &&
      product.saleStartDate &&
      product.saleEndDate &&
      now >= new Date(product.saleStartDate) &&
      now <= new Date(product.saleEndDate);

    // Sale discount takes priority if active
    if (isSaleActive) {
      return product.discountPercentage;
    }

    // First check if there's an explicit discount percentage
    if (product.discountType === 'percentage' && product.discountValue > 0) {
      return product.discountValue;
    }

    // Check if there's a fixed discount amount
    if (product.discountType === 'fixed' && product.discountValue > 0 && product.regularPrice > 0) {
      return Math.round((product.discountValue / product.regularPrice) * 100);
    }

    // Calculate discount from specialOfferPrice vs regularPrice
    if (product.specialOfferPrice && product.regularPrice && product.specialOfferPrice < product.regularPrice) {
      const discountAmount = product.regularPrice - product.specialOfferPrice;
      return Math.round((discountAmount / product.regularPrice) * 100);
    }

    // Calculate discount from price difference (fallback)
    const regularPrice = product.regularPrice || product.price;
    const displayPrice = getDisplayPrice();
    if (regularPrice && displayPrice && regularPrice > displayPrice) {
      const discountAmount = regularPrice - displayPrice;
      return Math.round((discountAmount / regularPrice) * 100);
    }

    // Return explicit discount field as last resort
    return product.discount > 0 ? product.discount : 0;
  };

  // Check if product has a discount
  const hasDiscount = () => {
    const discountPercentage = getDiscountPercentage();

    // Check if sale is active and within date range
    const now = new Date();
    const isSaleActive = product.isSaleActive &&
      product.discountPercentage &&
      product.saleStartDate &&
      product.saleEndDate &&
      now >= new Date(product.saleStartDate) &&
      now <= new Date(product.saleEndDate);

    return (
      discountPercentage > 0 &&
      (
        isSaleActive ||
        (product.discountValue > 0) ||
        (product.specialOfferPrice && product.regularPrice && product.specialOfferPrice < product.regularPrice) ||
        (product.discount > 0) ||
        (product.regularPrice && getDisplayPrice() < product.regularPrice)
      )
    );
  };

  // Check if product has near expiry discount
  const hasNearExpiryDiscount = () => {
    const now = new Date();
    return product.isNearExpiry &&
      product.expiryDate &&
      product.nearExpiryDiscountValue &&
      now <= new Date(product.expiryDate);
  };

  // Get near expiry badge text
  const getNearExpiryBadgeText = () => {
    if (!hasNearExpiryDiscount()) return null;

    if (product.nearExpiryDiscountType === 'percentage') {
      return `${product.nearExpiryDiscountValue}% OFF`;
    } else {
      return `Rs. ${product.nearExpiryDiscountValue} OFF`;
    }
  };

  // Get product ID safely (handling different ID formats)
  const getProductId = () => {
    return product._id || product.id;
  };

  // Safe access to product image
  const productImage = () => {
    if (imageError) return 'https://placehold.co/300x300/f8f9fa/a0aec0?text=No+Image';
    if (product.images && product.images.length > 0) {
      return getImageUrl(product.images[0]);
    }
    return getImageUrl(product.image) || 'https://placehold.co/300x300/f8f9fa/a0aec0?text=No+Image';
  };

  // Generate brand avatar initials
  const getBrandInitials = (brandName) => {
    if (!brandName) return 'BR';
    return brandName
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Handle wishlist action
  const handleWishlistAction = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isWishlistActionLoading) return;

    setIsWishlistActionLoading(true);
    try {
      if (isInWishlist(getProductId())) {
        await removeFromWishlist(getProductId());
      } else {
        await addToWishlist(product);
      }
    } catch (error) {
      console.error('Wishlist action failed:', error);
    } finally {
      setIsWishlistActionLoading(false);
    }
  };

  // Handle add to cart action
  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isCartActionLoading) return;

    // Guests can add to cart freely - addToCart keeps a local cart for them
    // and it gets merged into their account once they log in. Login is only
    // required at checkout (handleBuyNow below).
    setIsCartActionLoading(true);
    try {
      await addToCart(getProductId(), 1);
      if (isOutOfStock()) {
        toast.success("Added to cart — we'll email you when it's back in stock!");
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add item to cart');
    } finally {
      setIsCartActionLoading(false);
    }
  };

  // Handle buy now action
  const handleBuyNow = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isCartActionLoading) return;

    if (isOutOfStock()) {
      toast.error('This product is out of stock');
      return;
    }

    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/checkout' } } });
      return;
    }

    setIsCartActionLoading(true);
    try {
      if (!isInCart(getProductId())) {
        await addToCart(getProductId(), 1);
      }
      navigate('/checkout');
    } catch (error) {
      console.error('Error in buy now:', error);
      toast.error('Failed to process buy now');
    } finally {
      setIsCartActionLoading(false);
    }
  };

  // Handle card click for product details
  const handleCardClick = (e) => {
    // Don't navigate if clicking on action buttons
    if (e.target.closest('button') || e.target.closest('[role="button"]')) {
      return;
    }
    navigate(`/product/${product.slug}`);
  };

  // Safe number formatting
  const formatPrice = (price) => {
    return (price || 0).toLocaleString();
  };

  const displayPrice = getDisplayPrice();
  const discountPercentage = getDiscountPercentage();
  const hasDiscountBadge = hasDiscount();
  const isStockOut = (product.stock ?? 0) <= 0;
  const productId = getProductId();
  const isWishlisted = isInWishlist(productId);
  const isInCartState = isInCart(productId);

  return (
    <TooltipProvider>
        <div
          className={cn(
            "group relative bg-white rounded-2xl overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-lg transition-shadow duration-300 h-full flex flex-col border border-gray-100 cursor-pointer transform-gpu",
            className
          )}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={handleCardClick}
        >
          {/* Product Image Container (Strictly Uniform) */}
          <div className="relative w-full aspect-square bg-white overflow-hidden p-1">
            {!isImageLoaded && (
              <div className="absolute inset-0 bg-gray-50 animate-pulse" />
            )}

            {/* Hover Trigger removed */}
              <div className="block absolute inset-2">
                <div className="absolute inset-0">
                  <span className="sr-only">View {product.name}</span>
                </div>
                <img
                  src={productImage()}
                  alt={product.name}
                  className={cn(
                    "absolute inset-0 w-full h-full object-contain mix-blend-multiply transition-all duration-500 ease-out transform-gpu will-change-transform",
                    isHovered ? "scale-105" : "scale-100",
                    isImageLoaded ? "opacity-100" : "opacity-0"
                  )}
                  onLoad={() => setIsImageLoaded(true)}
                  onError={() => {
                    setImageError(true);
                    setIsImageLoaded(true);
                  }}
                  loading="lazy"
                />
              </div>

            {/* Badges and Actions */}
            <div className="absolute top-3 left-3 right-3 flex justify-between items-start pointer-events-none">
              <div className="flex flex-col gap-2">
                {isStockOut ? (
                  <div className="bg-[#1B365D] text-white text-[11px] font-bold px-2.5 py-1 rounded-[4px] shadow-sm tracking-wide">
                    OUT OF STOCK
                  </div>
                ) : hasNearExpiryDiscount() ? (
                  <div className="bg-orange-500 text-white text-[11px] font-bold px-2.5 py-1 rounded-[4px] shadow-sm tracking-wide flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Near Expiry
                  </div>
                ) : (hasDiscountBadge && discountPercentage > 0) ? (
                  <div className="bg-secondary-600 text-white text-[11px] font-bold px-2.5 py-1 rounded-[4px] shadow-sm tracking-wide">
                    {discountPercentage}% OFF
                  </div>
                ) : product.isNew ? (
                  <div className="bg-green-500 text-white text-[11px] font-bold px-2.5 py-1 rounded-[4px] shadow-sm tracking-wide">
                    NEW
                  </div>
                ) : null}
              </div>

              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    className={cn(
                      "h-7 w-7 rounded-full shadow-sm transition-all duration-200 flex items-center justify-center pointer-events-auto",
                      isWishlisted
                        ? "bg-white text-secondary-600"
                        : "bg-white text-gray-800 hover:text-secondary-600"
                    )}
                    onClick={handleWishlistAction}
                    disabled={isWishlistActionLoading}
                  >
                    {isWishlistActionLoading ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    ) : isWishlisted ? (
                      <Heart className="h-3.5 w-3.5 fill-current" />
                    ) : (
                      <HeartIcon className="h-3.5 w-3.5" />
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{isWishlisted ? "Remove from wishlist" : "Add to wishlist"}</p>
                  <TooltipArrow />
                </TooltipContent>
              </Tooltip>
            </div>
          </div>

          {/* Product Information */}
          <div className="flex-grow flex flex-col p-1.5">
            {/* Product Name */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <h3 className="text-sm font-bold text-black mb-0.5 line-clamp-1 leading-tight hover:text-gray-700 transition-colors text-left">
                    {product.name}
                  </h3>
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>{product.name}</p>
                <TooltipArrow />
              </TooltipContent>
            </Tooltip>

            {/* Short Description */}
            <p className="text-[11px] text-[#6B7280] line-clamp-1 mb-1.5 leading-snug text-left">
              {product.shortDescription || product.description || "Product details"}
            </p>

            {/* Rating */}
            <div className="flex items-center gap-1 mb-1.5">
              {renderStars(product.rating)}
              <span className="text-[11px] font-bold text-[#111827] ml-1">
                {(product.rating || 0).toFixed(1)} 
              </span>
              <span className="text-[11px] text-[#6B7280]">
                ({product.reviewCount || 0})
              </span>
            </div>

            <div className="w-full h-px bg-gray-100 mb-1.5"></div>

            {/* Bottom Section */}
            <div className="mt-auto flex flex-col gap-1.5">
              {/* Price */}
              <div className="flex flex-row items-baseline gap-1.5">
                <span className="text-[16px] font-bold text-[#111827] leading-none">
                  Rs.{formatPrice(displayPrice)}
                </span>
                <span className="text-[11px] text-[#6B7280] line-through font-medium">
                  Rs.{formatPrice(product.regularPrice || product.price)}
                </span>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1.5 w-full mt-1">
                <button
                  className="flex-1 h-7 rounded-[6px] border border-[#1B365D] text-[#1B365D] bg-white text-[10px] font-bold hover:bg-gray-50 transition-colors pointer-events-auto disabled:opacity-50"
                  onClick={handleBuyNow}
                  disabled={isOutOfStock() || isCartActionLoading}
                >
                  Buy Now
                </button>
                <button
                  className={cn(
                    "w-7 h-7 rounded-[6px] flex items-center justify-center transition-colors pointer-events-auto",
                    isInCartState
                      ? "bg-green-600 text-white"
                      : "bg-[#1B365D] text-white hover:bg-[#152A4A]"
                  )}
                  onClick={handleAddToCart}
                  disabled={isCartActionLoading}
                >
                  {isCartActionLoading ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  ) : (
                    <CartPlusIcon className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
    </TooltipProvider>
  );
};

export default ProductCard;